import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Plus, Variable, Pencil, Trash2, AlertTriangle, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { value: 'contrato', label: 'Contrato' },
  { value: 'equipamento', label: 'Equipamento' },
  { value: 'recurso', label: 'Recurso Humano' },
  { value: 'custo', label: 'Custo' },
  { value: 'inventario', label: 'Inventário' },
  { value: 'financeiro', label: 'Financeiro' },
  { value: 'geral', label: 'Geral' },
];

const EMPTY_FORM = { name: '', label: '', description: '', default_value: '', category: 'geral' };

const VariablesPage = () => {
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');

  const loadVariables = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/variables`, { withCredentials: true });
      setVariables(res.data);
    } catch (error) {
      console.error('Failed to load variables:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVariables(); }, [loadVariables]);

  const openCreate = () => {
    setEditing(null);
    setFormData({ ...EMPTY_FORM });
    setDialogOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    setFormData({ name: v.name, label: v.label, description: v.description || '', default_value: v.default_value || '', category: v.category || 'geral' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/variables/${editing.var_id}`, { label: formData.label, description: formData.description, default_value: formData.default_value, category: formData.category }, { withCredentials: true });
        toast.success('Variavel atualizada');
      } else {
        await axios.post(`${API_URL}/variables`, formData, { withCredentials: true });
        toast.success('Variavel criada');
      }
      setDialogOpen(false);
      loadVariables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (varId) => {
    try {
      await axios.delete(`${API_URL}/variables/${varId}`, { withCredentials: true });
      toast.success('Variavel excluida');
      setDeleteTarget(null);
      loadVariables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const filtered = variables.filter(v => {
    const matchSearch = !search || v.name.includes(search.toUpperCase()) || v.label.toLowerCase().includes(search.toLowerCase());
    const matchCat = !filterCat || v.category === filterCat;
    return matchSearch && matchCat;
  });

  // Group by category
  const grouped = {};
  filtered.forEach(v => {
    const cat = v.category || 'geral';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(v);
  });

  if (loading) {
    return <div className="flex justify-center p-12"><div className="w-12 h-12 border-4 border-[#AF2628] border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="variables-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#111111]">Variaveis de Formula</h1>
          <p className="text-[#6E6E73]">Catalogo de variaveis disponiveis para formulas de precificacao</p>
        </div>
        <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]" data-testid="new-variable-button">
          <Plus className="w-4 h-4 mr-2" />Nova Variavel
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#AEAEB2]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nome ou descricao..." className="pl-10" data-testid="variable-search" />
        </div>
        <Select value={filterCat || 'all'} onValueChange={(val) => setFilterCat(val === 'all' ? '' : val)}>
          <SelectTrigger className="w-48"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {Object.entries(grouped).length > 0 ? (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, vars]) => {
            const catLabel = CATEGORIES.find(c => c.value === cat)?.label || cat;
            return (
              <div key={cat} className="bg-white border border-[#E5E5EA] rounded-xl p-5">
                <h3 className="text-sm font-bold text-[#6E6E73] uppercase tracking-wide mb-3">{catLabel}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {vars.map(v => (
                    <div key={v.var_id} className="border border-[#E5E5EA] rounded-lg p-3 hover:border-[#AF2628]/30 transition-colors group" data-testid={`variable-card-${v.name}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <code className="text-sm font-bold text-[#AF2628] font-mono">{v.name}</code>
                          <p className="text-sm text-[#111111] mt-1">{v.label}</p>
                          {v.description && <p className="text-xs text-[#AEAEB2] mt-0.5">{v.description}</p>}
                          {v.default_value && <p className="text-xs text-[#6E6E73] mt-1">Padrao: <span className="font-medium">{v.default_value}</span></p>}
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(v)}><Pencil className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setDeleteTarget(v)}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-white border border-[#E5E5EA] rounded-xl">
          <Variable className="w-12 h-12 text-[#AEAEB2] mx-auto mb-3" />
          <p className="text-[#6E6E73] mb-4">Nenhuma variavel cadastrada</p>
          <Button onClick={openCreate} className="bg-[#AF2628] hover:bg-[#961B22]"><Plus className="w-4 h-4 mr-2" />Criar Primeira Variavel</Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? 'Editar Variavel' : 'Nova Variavel'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="variable-form">
            <div>
              <Label>Nome da Variavel *</Label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') })}
                placeholder="Ex: QTD_UGS"
                className="font-mono"
                disabled={!!editing}
                data-testid="variable-name-input"
              />
              <p className="text-[10px] text-[#AEAEB2] mt-1">Letras maiusculas, numeros e underscores</p>
            </div>
            <div>
              <Label>Descricao curta *</Label>
              <Input required value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} placeholder="Ex: Quantidade de UGs" data-testid="variable-label-input" />
            </div>
            <div>
              <Label>Descricao detalhada</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Explicacao de quando usar esta variavel" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor padrao</Label>
                <Input value={formData.default_value} onChange={(e) => setFormData({ ...formData, default_value: e.target.value })} placeholder="Ex: 12" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#AF2628] hover:bg-[#961B22]" disabled={submitting} data-testid="variable-submit-button">
                {submitting ? 'Salvando...' : (editing ? 'Salvar' : 'Cadastrar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-600" />Excluir Variavel</DialogTitle>
            <DialogDescription>Excluir a variavel <code className="font-bold">{deleteTarget?.name}</code>? Formulas que a utilizam podem parar de funcionar.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(deleteTarget?.var_id)}>Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VariablesPage;
